import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { AgGridAngular } from 'ag-grid-angular';
import {
  ColDef,
  FilterChangedEvent,
  FirstDataRenderedEvent,
  GridApi,
  GridReadyEvent,
  GridSizeChangedEvent,
  SelectionChangedEvent,
} from 'ag-grid-community';
import {
  HlmBtnDirective,
  HlmIconComponent,
  HlmInputDirective,
  HlmSpinnerComponent,
  ToastService,
} from '../../../ui';
import { CmtFlagComponent } from '../../../ui/flag.component';
import { isoForVorwahl } from '../country-flag.util';
import { PermissionService } from '../../../core/auth/permission.service';
import { RufnummerService } from '../rufnummer/rufnummer.service';
import { NumberBlock } from '../rufnummer/rufnummer.model';
import {
  AttachNumberBlocksModalComponent,
  AttachNumberBlocksModalData,
} from '../rufnummer/rufnummer-modal.component';
import { ConfirmDialogComponent } from '../../../shared/confirm-dialog/confirm-dialog.component';
import { CountryFlagCellComponent } from './country-flag-cell.component';

/**
 * Embeddable per-brand Number Blocks management panel — the linked-blocks grid
 * (desktop AG-Grid + mobile card mode), a search filter, multi-row selection,
 * a "Link Number Blocks" attach button, a confirm-gated bulk detach, and
 * `brandManager.write` gating, with NO page hero / breadcrumb / read-only
 * banner. Reused by both {@link BrandNumberBlocksPageComponent} (standalone
 * route `/admin/brands/:brandId/numberblocks`) and the brand detail/edit page.
 *
 * Mirrors {@link BrandDomainsPanelComponent}: owns load/reload via the service,
 * reacts to a changing `brandId` input via a constructor `effect()` (Pitfall
 * #13), and sizes its desktop grid in a definite-height wrapper (`h-[28rem]`)
 * with AG-Grid's normal layout so the grid scrolls internally and pins the
 * column header (Pitfall #1: normal layout needs a concrete pixel height).
 */
@Component({
  selector: 'cmt-brand-number-blocks-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AgGridAngular,
    HlmBtnDirective,
    HlmIconComponent,
    HlmInputDirective,
    HlmSpinnerComponent,
    CmtFlagComponent,
  ],
  templateUrl: './brand-number-blocks-panel.component.html',
  styleUrl: './brand-number-blocks-panel.component.scss',
})
export class BrandNumberBlocksPanelComponent {
  /** Owning brand — drives the load and all mutations. */
  readonly brandId = input.required<number>();
  /** Human-readable name for dialog copy; falls back to `Brand #<id>`. */
  readonly brandName = input<string>('');

  private readonly dialog = inject(MatDialog);
  private readonly toastService = inject(ToastService);
  private readonly rufnummerService = inject(RufnummerService);
  private readonly permissionService = inject(PermissionService);
  private readonly destroyRef = inject(DestroyRef);

  /** EB-3709: disable mutating actions for read-only users (no amie.brandManager.write). */
  protected readonly canWrite = computed(() => this.permissionService.canWrite('brandManager'));

  protected readonly blocks = signal<NumberBlock[]>([]);
  protected readonly loading = signal(true);
  protected readonly filterText = signal('');
  protected readonly selected = signal<readonly NumberBlock[]>([]);
  /** True when any AG-Grid column filter is active — drives the "Clear filters" button. */
  protected readonly hasColumnFilter = signal(false);

  private gridApi: GridApi<NumberBlock> | null = null;

  /** Dialog copy needs a non-empty label even when no name was supplied. */
  private readonly displayName = computed(() => this.brandName() || `Brand #${this.brandId()}`);

  protected readonly filteredBlocks = computed(() => {
    const q = this.filterText().trim().toLowerCase();
    const list = this.blocks();
    if (!q) return list;
    return list.filter(b =>
      b.vorwahl.toLowerCase().includes(q) ||
      b.prefix.toLowerCase().includes(q) ||
      b.carrier.toLowerCase().includes(q) ||
      b.staOrt.toLowerCase().includes(q) ||
      b.staLand.toLowerCase().includes(q),
    );
  });

  protected readonly count = computed(() => this.blocks().length);
  protected readonly visibleCount = computed(() => this.filteredBlocks().length);
  protected readonly selectedCount = computed(() => this.selected().length);

  // ---- Mobile card-mode state -------------------------------------------------
  // Many entries are too costly to render eagerly on phones. Cap at 100 cards
  // and surface a "Show 100 more" button. Search is the primary tool to narrow.
  private static readonly MOBILE_PAGE_SIZE = 100;
  protected readonly mobileLimit = signal(BrandNumberBlocksPanelComponent.MOBILE_PAGE_SIZE);
  protected readonly mobileVisibleBlocks = computed(() =>
    this.filteredBlocks().slice(0, this.mobileLimit()),
  );
  protected readonly mobileHasMore = computed(
    () => this.filteredBlocks().length > this.mobileVisibleBlocks().length,
  );

  /** Set-backed selection lookup so card hit-testing is O(1) per render. */
  private readonly selectedKeySet = computed(
    () => new Set(this.selected().map(b => b.key)),
  );

  /** Iso-2 country code for the flag in card mode. */
  protected isoForBlock(block: NumberBlock): string | null {
    return isoForVorwahl(block.vorwahl);
  }

  protected isCardSelected(block: NumberBlock): boolean {
    return this.selectedKeySet().has(block.key);
  }

  protected toggleCardSelect(block: NumberBlock): void {
    const current = this.selected();
    if (this.selectedKeySet().has(block.key)) {
      this.selected.set(current.filter(b => b.key !== block.key));
    } else {
      this.selected.set([...current, block]);
    }
  }

  protected loadMoreMobile(): void {
    this.mobileLimit.update(n => n + BrandNumberBlocksPanelComponent.MOBILE_PAGE_SIZE);
  }

  /**
   * Tristate for the mobile master-checkbox.
   * - 'all'     → every filtered block is selected (master renders as checked)
   * - 'partial' → some filtered blocks are selected (master renders as dash)
   * - 'none'    → none of the filtered blocks are selected
   */
  protected readonly mobileSelectionState = computed<'none' | 'partial' | 'all'>(() => {
    const filtered = this.filteredBlocks();
    if (filtered.length === 0) return 'none';
    const selectedSet = this.selectedKeySet();
    let hits = 0;
    let misses = 0;
    for (const b of filtered) {
      if (selectedSet.has(b.key)) hits++;
      else misses++;
      // Early-exit only when we've genuinely seen both — finding one hit
      // mid-sweep doesn't mean partial; the remaining items might all match.
      if (hits > 0 && misses > 0) return 'partial';
    }
    return hits === filtered.length ? 'all' : 'none';
  });

  protected toggleSelectAllMobile(): void {
    if (this.mobileSelectionState() === 'all') {
      // Preserve any currently-selected blocks that are *not* in the filtered
      // view, so the user doesn't lose their selection by clearing while a
      // search filter is applied.
      const filteredSet = new Set(this.filteredBlocks().map(b => b.key));
      this.selected.set(this.selected().filter(b => !filteredSet.has(b.key)));
      return;
    }
    // Union: keep existing selection (may include items outside the filter)
    // and add every filtered block.
    const existing = this.selected();
    const existingSet = this.selectedKeySet();
    const additions = this.filteredBlocks().filter(b => !existingSet.has(b.key));
    this.selected.set([...existing, ...additions]);
  }

  protected readonly columnDefs: ColDef<NumberBlock>[] = [
    {
      headerName: '',
      width: 44,
      minWidth: 44,
      maxWidth: 44,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      sortable: false,
      filter: false,
      resizable: false,
      pinned: 'left',
    },
    {
      headerName: 'Country',
      minWidth: 160,
      flex: 1.2,
      valueGetter: p => p.data?.staLand ?? '',
      cellRenderer: CountryFlagCellComponent,
    },
    { field: 'vorwahl', headerName: 'Area code', minWidth: 110, maxWidth: 140, cellClass: 'cmt-mono' },
    { field: 'prefix', headerName: 'Prefix', minWidth: 110, maxWidth: 140, cellClass: 'cmt-mono' },
    { field: 'staOrt', headerName: 'Location', minWidth: 160, flex: 1.2 },
    { field: 'carrier', headerName: 'Carrier', minWidth: 150, flex: 1 },
  ];

  protected readonly defaultColDef: ColDef = {
    sortable: true,
    resizable: true,
    filter: 'agTextColumnFilter',
    // Single-condition "contains" only — kills AND/OR + operator dropdown,
    // leaving just one input field per column with a Clear button.
    filterParams: {
      maxNumConditions: 1,
      filterOptions: ['contains'],
      buttons: ['clear'],
    },
  };

  /** Stable row identity so AG-Grid preserves selection across `rowData` refreshes. */
  protected readonly getRowId = (params: { data: NumberBlock }) => params.data.key;

  constructor() {
    // Reload whenever the bound brandId changes. effect (not subscribe) so the
    // panel reacts to a parent swapping the input — Pitfall #13.
    effect(() => {
      const id = this.brandId();
      if (id == null || Number.isNaN(id)) return;
      // Reset selection/paging when the brand changes, so stale selections from
      // a previous brand can't bleed into the new brand's detach action.
      this.selected.set([]);
      this.mobileLimit.set(BrandNumberBlocksPanelComponent.MOBILE_PAGE_SIZE);
      this.reload();
    });
  }

  protected reload(): void {
    const id = this.brandId();
    if (id == null || Number.isNaN(id)) return;
    this.loading.set(true);
    this.rufnummerService
      .listLinked(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: list => {
        this.blocks.set(list);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.toastService.open('Failed to load number blocks', 'OK', { duration: 5000 });
      },
    });
  }

  protected onGridReady(event: GridReadyEvent<NumberBlock>): void {
    this.gridApi = event.api;
    // Replay any selection captured by the mobile-card layout into the grid
    // so a viewport switch (mobile → desktop) doesn't visually drop selected
    // rows. Both views write to the same `selected()` signal.
    const keys = this.selectedKeySet();
    if (keys.size > 0) {
      event.api.forEachNode(node => {
        if (node.data && keys.has(node.data.key)) node.setSelected(true);
      });
    }
  }

  protected onFirstDataRendered(event: FirstDataRenderedEvent): void {
    event.api.sizeColumnsToFit();
  }

  protected onGridSizeChanged(event: GridSizeChangedEvent): void {
    event.api.sizeColumnsToFit();
  }

  protected onSelectionChanged(_event: SelectionChangedEvent<NumberBlock>): void {
    const rows = this.gridApi?.getSelectedRows() ?? [];
    this.selected.set(rows);
  }

  protected onFilterChanged(event: FilterChangedEvent<NumberBlock>): void {
    const model = event.api.getFilterModel();
    this.hasColumnFilter.set(model != null && Object.keys(model).length > 0);
  }

  protected clearColumnFilters(): void {
    this.gridApi?.setFilterModel(null);
  }

  protected openAttach(): void {
    // EB-3709 M1: belt-and-braces canWrite guard against programmatic invocation.
    if (!this.permissionService.canWrite('brandManager')) return;
    const id = this.brandId();
    if (id == null) return;
    const data: AttachNumberBlocksModalData = {
      brandId: id,
      brandName: this.displayName(),
    };
    this.dialog
      .open(AttachNumberBlocksModalComponent, {
        data,
        width: '760px',
        maxWidth: '92vw',
        autoFocus: false,
        panelClass: 'cmt-dialog-panel',
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(mutated => {
        if (mutated) this.reload();
      });
  }

  /**
   * Gate `detachSelected` behind a confirm dialog — unlink reaches into a
   * brand's live phone-routing config and is a misclick away when many rows
   * are selected.
   */
  protected confirmDetach(): void {
    // EB-3709 M1: belt-and-braces canWrite guard against programmatic invocation.
    if (!this.permissionService.canWrite('brandManager')) return;
    const id = this.brandId();
    const selected = this.selected();
    if (id == null || selected.length === 0) return;
    const n = selected.length;
    const brandLabel = this.displayName();
    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: n === 1 ? 'Unlink number block?' : `Unlink ${n} number blocks?`,
          message: n === 1
            ? `${selected[0].vorwahl} ${selected[0].prefix} (${selected[0].staOrt || '—'}) will be removed from ${brandLabel}. The block stays in the carrier pool and can be linked again later.`
            : `${n} blocks will be removed from ${brandLabel}. They stay in the carrier pool and can be linked again later.`,
          confirmLabel: n === 1 ? 'Unlink' : `Unlink ${n}`,
          cancelLabel: 'Cancel',
          confirmColor: 'warn',
          icon: 'warning',
        },
        width: '460px',
        maxWidth: '92vw',
        autoFocus: false,
        panelClass: 'cmt-dialog-panel',
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmed => {
        if (confirmed) this.detachSelected();
      });
  }

  private detachSelected(): void {
    const id = this.brandId();
    const selected = this.selected();
    if (id == null || selected.length === 0) return;
    this.rufnummerService
      .detach(id, selected)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: deleted => {
        const n = deleted.length;
        this.toastService.open(
          `${n} block${n === 1 ? '' : 's'} unlinked`,
          'OK',
          { duration: 3000 },
        );
        this.selected.set([]);
        this.reload();
      },
      error: () => {
        this.toastService.open('Unlink failed — please retry.', 'OK', { duration: 4000 });
      },
    });
  }
}
